import { useLazyQuery, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import SearchBar from "components/base/SearchBar";
import Spinner, { InlineSpinner } from "components/base/Spinner";
import { ProductImagesV2 } from "components/shared/ProductImages";
import { useModals } from "ModalProvider";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MdCalendarMonth,
  MdExpandMore,
  MdFactory,
  MdRefresh,
} from "react-icons/md";
import { RiShip2Line } from "react-icons/ri";
import CreateReport from "./CreateReport";
import TodoDateRangeSelect from "./components/TodoDateRangeSelect";
import { FETCH_QC_TODO_LINES_BULK, FETCH_QC_TODO_LIST } from "./graphql";
import RecentQCReports from "./RecentReports";

const VIEW_MODES = {
  SOURCE: "source",
  FACTORY: "factory",
  DATE_TABLE: "date-table",
};

const EMPTY_LINE_STATE = { loading: false, lines: [], error: null };
const TODO_LIST_VARIABLES = {
  days: 7,
  warehouseIds: [1, 18],
  limit: 2000,
  forceRefresh: false,
};
const TODO_DATE_RANGE_OPTIONS = [
  { days: 7, label: "7天前起" },
  { days: 30, label: "30天前起" },
];

const getSourceKey = (source) => source.sourceType + "-" + source.id;
const getTodoListKey = (days, todos) =>
  `${days}:${todos.map(getSourceKey).join(",")}`;

const parseDeliveryDate = (deliveryDate) => {
  if (!deliveryDate) return null;
  const m = moment(deliveryDate);
  return m.isValid() ? m : null;
};

const sortByDeliveryDate = (a, b) => {
  const aDate = parseDeliveryDate(a.todo.deliveryDate);
  const bDate = parseDeliveryDate(b.todo.deliveryDate);
  if (!aDate && !bDate) return a.todo.id - b.todo.id;
  if (!aDate) return 1;
  if (!bDate) return -1;
  if (aDate.valueOf() === bDate.valueOf()) return a.todo.id - b.todo.id;
  return aDate.valueOf() - bDate.valueOf();
};

const Dashboard = () => {
  const modal = useModals();
  const [days, setDays] = useState(TODO_LIST_VARIABLES.days);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState(VIEW_MODES.SOURCE);
  const [expandedTodoKeys, setExpandedTodoKeys] = useState(new Set());
  const [expandedFactoryKeys, setExpandedFactoryKeys] = useState(new Set());
  const [lineCache, setLineCache] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const bulkPrefetchedRef = useRef(null);
  const lineRequestVersionRef = useRef(0);

  const { loading, error, data, refetch } = useQuery(FETCH_QC_TODO_LIST, {
    variables: { ...TODO_LIST_VARIABLES, days },
  });

  const [fetchTodoLinesBulk] = useLazyQuery(FETCH_QC_TODO_LINES_BULK, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  const todoList = data?.qcTodoList ?? [];
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const filteredTodoEntries = useMemo(() => {
    if (!normalizedSearchQuery) {
      return todoList.map((todo) => ({ todo, filteredLines: null }));
    }

    return todoList.reduce((acc, todo) => {
      const sourceName = (todo.name || "").toLowerCase();
      const invoiceNumber = (todo.invoiceNumber || "").toLowerCase();
      const customerName = (todo.customerName || "").toLowerCase();
      const lines = lineCache[getSourceKey(todo)]?.lines || [];
      const sourceMatched =
        sourceName.includes(normalizedSearchQuery) ||
        invoiceNumber.includes(normalizedSearchQuery) ||
        customerName.includes(normalizedSearchQuery);

      if (sourceMatched) {
        acc.push({ todo, filteredLines: null });
        return acc;
      }

      const filteredLines = lines.filter((row) => {
        const po = (row.po || "").toLowerCase();
        const productName = (row.product?.name || "").toLowerCase();
        const productNumber = (row.product?.number || "").toLowerCase();
        return (
          po.includes(normalizedSearchQuery) ||
          productName.includes(normalizedSearchQuery) ||
          productNumber.includes(normalizedSearchQuery)
        );
      });

      if (filteredLines.length > 0) {
        acc.push({ todo, filteredLines });
      }

      return acc;
    }, []);
  }, [lineCache, normalizedSearchQuery, todoList]);

  const loadTodoLines = useCallback(
    (todos, forceRefresh = false) => {
      if (todos.length === 0) return Promise.resolve();
      const todoListKey = getTodoListKey(days, todos);
      const requestVersion = lineRequestVersionRef.current;
      const allLineSources = Array.from(
        new Map(
          todos.flatMap((todo) =>
            (todo.orderLineIds || []).map((lineId) => [
              todo.sourceType + "-" + lineId,
              { sourceType: todo.sourceType, lineId },
            ]),
          ),
        ).values(),
      );
      if (allLineSources.length === 0) {
        if (requestVersion !== lineRequestVersionRef.current) {
          return Promise.resolve();
        }
        bulkPrefetchedRef.current = todoListKey;
        setLineCache((prev) => {
          const next = { ...prev };
          todos.forEach((todo) => {
            next[getSourceKey(todo)] = {
              loading: false,
              lines: [],
              error: null,
            };
          });
          return next;
        });
        return Promise.resolve();
      }

      setLineCache((prev) => {
        const next = { ...prev };
        todos.forEach((todo) => {
          next[getSourceKey(todo)] = { loading: true, lines: [], error: null };
        });
        return next;
      });

      return fetchTodoLinesBulk({
        variables: { lineSources: allLineSources, forceRefresh },
      })
        .then((res) => {
          if (requestVersion !== lineRequestVersionRef.current) return;
          const rows = res?.data?.qcTodoLinesBulk || [];
          const grouped = {};
          rows.forEach((row) => {
            const key = row.sourceType + "-" + row.sourceId;
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(row);
          });

          setLineCache((prev) => {
            const next = { ...prev };
            todos.forEach((todo) => {
              next[getSourceKey(todo)] = {
                loading: false,
                lines: grouped[getSourceKey(todo)] || [],
                error: null,
              };
            });
            return next;
          });
          bulkPrefetchedRef.current = todoListKey;
        })
        .catch((err) => {
          if (requestVersion !== lineRequestVersionRef.current) return;
          setLineCache((prev) => {
            const next = { ...prev };
            todos.forEach((todo) => {
              next[getSourceKey(todo)] = {
                loading: false,
                lines: [],
                error: err.message,
              };
            });
            return next;
          });
          bulkPrefetchedRef.current = todoListKey;
        });
    },
    [days, fetchTodoLinesBulk],
  );

  useEffect(() => {
    const todoListKey = getTodoListKey(days, todoList);
    if (
      loading ||
      bulkPrefetchedRef.current === true ||
      bulkPrefetchedRef.current === todoListKey ||
      todoList.length === 0
    ) {
      return;
    }
    loadTodoLines(todoList);
  }, [days, loadTodoLines, loading, todoList]);

  const changeDays = (nextDays) => {
    if (nextDays === days) return;
    lineRequestVersionRef.current += 1;
    bulkPrefetchedRef.current = null;
    setLineCache({});
    setDays(nextDays);
  };

  const refreshTodo = async () => {
    if (refreshing) return;
    setRefreshing(true);
    lineRequestVersionRef.current += 1;
    bulkPrefetchedRef.current = true;
    setLineCache({});

    try {
      const res = await refetch({
        ...TODO_LIST_VARIABLES,
        days,
        forceRefresh: true,
      });
      const refreshedTodos = res?.data?.qcTodoList || [];
      await loadTodoLines(refreshedTodos, true);
    } finally {
      setRefreshing(false);
    }
  };

  const entriesWithState = useMemo(() => {
    return filteredTodoEntries
      .map(({ todo, filteredLines }) => {
        const lineState = lineCache[getSourceKey(todo)] || EMPTY_LINE_STATE;
        const visibleLines = filteredLines || lineState.lines;
        return { todo, lineState, visibleLines };
      })
      .filter(
        (entry) =>
          entry.lineState.loading ||
          entry.lineState.error ||
          entry.visibleLines.length > 0,
      )
      .sort(sortByDeliveryDate);
  }, [filteredTodoEntries, lineCache]);

  const factoryGroups = useMemo(() => {
    const groups = new Map();

    entriesWithState.forEach((entry) => {
      if (
        entry.lineState.loading ||
        entry.lineState.error ||
        entry.visibleLines.length === 0
      ) {
        return;
      }

      const rowsByFactory = new Map();
      entry.visibleLines.forEach((row) => {
        const factory = row.product?.packingFactory;
        const key = factory?.id ? `factory-${factory.id}` : "factory-none";
        if (!rowsByFactory.has(key)) {
          rowsByFactory.set(key, {
            key,
            displayName: factory?.name || "未关联包装工厂",
            factory,
            lines: [],
          });
        }
        rowsByFactory.get(key).lines.push(row);
      });

      rowsByFactory.forEach((groupInfo) => {
        if (!groups.has(groupInfo.key)) {
          groups.set(groupInfo.key, {
            key: groupInfo.key,
            displayName: groupInfo.displayName,
            factory: groupInfo.factory,
            entries: [],
          });
        }

        groups.get(groupInfo.key).entries.push({
          todo: entry.todo,
          lineState: entry.lineState,
          visibleLines: groupInfo.lines,
        });
      });
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        entries: group.entries.sort(sortByDeliveryDate),
        itemCount: group.entries.reduce(
          (sum, entry) => sum + entry.visibleLines.length,
          0,
        ),
      }))
      .filter((group) => group.entries.length > 0)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [entriesWithState]);

  const dateColumns = useMemo(() => {
    const byDate = new Map();

    entriesWithState.forEach((entry) => {
      const raw = entry.todo.deliveryDate;
      const key = raw ? moment(raw).format("YYYY-MM-DD") : "unknown";
      if (!byDate.has(key)) {
        byDate.set(key, {
          key,
          raw,
          label: raw ? moment(raw).format("YYYY-MM-DD") : "未知日期",
        });
      }
    });

    const values = Array.from(byDate.values());
    const known = values
      .filter((i) => i.raw)
      .sort((a, b) => moment(a.raw).valueOf() - moment(b.raw).valueOf());
    const unknown = values.filter((i) => !i.raw);
    return [...known, ...unknown];
  }, [entriesWithState]);

  const hasAnyLineLoading = entriesWithState.some(
    (entry) => entry.lineState.loading,
  );

  const toggleTodo = (todo) => {
    const todoKey = getSourceKey(todo);
    const next = new Set(expandedTodoKeys);
    if (next.has(todoKey)) {
      next.delete(todoKey);
      setExpandedTodoKeys(next);
      return;
    }
    next.add(todoKey);
    setExpandedTodoKeys(next);
  };

  const toggleFactory = (key) => {
    const next = new Set(expandedFactoryKeys);
    if (next.has(key)) {
      next.delete(key);
      setExpandedFactoryKeys(next);
      return;
    }
    next.add(key);
    setExpandedFactoryKeys(next);
  };

  const openDateDetails = (group, column) => {
    const getDateKey = (deliveryDate) => {
      if (!deliveryDate) return "unknown";
      return moment(deliveryDate).format("YYYY-MM-DD");
    };

    const matchedEntries = group.entries.filter(
      (entry) => getDateKey(entry.todo.deliveryDate) === column.key,
    );

    if (matchedEntries.length === 0) return;

    modal.present({
      title: `${group.displayName} - ${column.label}`,
      center: true,
      children: (
        <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2 bg-gray-100 dark:bg-gray-900/50 p-4 rounded-xl">
          {matchedEntries.map((entry) => (
            <TodoDetailBlock
              key={`${group.key}-${getSourceKey(entry.todo)}`}
              todo={entry.todo}
              lines={entry.visibleLines}
              modal={modal}
            />
          ))}
        </div>
      ),
    });
  };

  if (loading && !refreshing) return <Spinner />;
  if (error) return <Errors error={error} />;

  return (
    <div className="p-3 md:p-6 md:h-[90vh] lg:h-[95vh] flex flex-col rounded-lg gap-4">
      <h2 className="hidden lg:block">To-do</h2>
      <div className="md:flex md:space-x-6 space-y-6 md:space-y-0 flex-1 overflow-hidden">
        <div className="h-[78vh] md:h-[90vh] lg:h-full w-full lg:w-2/3 flex flex-col overflow-hidden">
          <div className="sticky top-0 z-10 pb-4">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
              <SearchBar
                placeholder="SO、PO、产品、客户"
                initialQuery={searchQuery}
                onChange={setSearchQuery}
                containerClassName="w-full min-w-0 md:flex-1"
              />
              <div className="flex items-center gap-2 md:shrink-0">
                <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800 h-[30px]">
                  <button
                    type="button"
                    onClick={() => setViewMode(VIEW_MODES.SOURCE)}
                    aria-pressed={viewMode === VIEW_MODES.SOURCE}
                    className={`h-full px-3 flex items-center text-sm transition-colors ${
                      viewMode === VIEW_MODES.SOURCE
                        ? "bg-blue-50 text-blue-600 dark:bg-gray-700 dark:text-blue-300"
                        : "text-gray-600 dark:text-gray-300"
                    }`}
                    title="单据视图"
                  >
                    <RiShip2Line className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode(VIEW_MODES.FACTORY)}
                    aria-pressed={viewMode === VIEW_MODES.FACTORY}
                    className={`h-full px-3 flex items-center text-sm transition-colors border-l border-gray-200 dark:border-gray-700 ${
                      viewMode === VIEW_MODES.FACTORY
                        ? "bg-blue-50 text-blue-600 dark:bg-gray-700 dark:text-blue-300"
                        : "text-gray-600 dark:text-gray-300"
                    }`}
                    title="包装工厂视图"
                  >
                    <MdFactory className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode(VIEW_MODES.DATE_TABLE)}
                    aria-pressed={viewMode === VIEW_MODES.DATE_TABLE}
                    className={`h-full px-3 flex items-center text-sm transition-colors border-l border-gray-200 dark:border-gray-700 ${
                      viewMode === VIEW_MODES.DATE_TABLE
                        ? "bg-blue-50 text-blue-600 dark:bg-gray-700 dark:text-blue-300"
                        : "text-gray-600 dark:text-gray-300"
                    }`}
                    title="日期表格视图"
                  >
                    <MdCalendarMonth className="w-4 h-4" />
                  </button>
                </div>
                <TodoDateRangeSelect
                  days={days}
                  disabled={loading || refreshing}
                  onChange={changeDays}
                  options={TODO_DATE_RANGE_OPTIONS}
                />
                <button
                  type="button"
                  onClick={refreshTodo}
                  disabled={loading || refreshing}
                  className="h-[30px] w-[30px] shrink-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                  title="重新从 Odoo 刷新待办"
                  aria-label="重新从 Odoo 刷新待办"
                >
                  <MdRefresh
                    className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
                  />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto space-y-3 pr-0 md:pr-3">
            {entriesWithState.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                暂无符合条件的待办单据
              </div>
            ) : viewMode === VIEW_MODES.SOURCE ? (
              entriesWithState.map((entry) => (
                <TodoCard
                  key={getSourceKey(entry.todo)}
                  todo={entry.todo}
                  lineState={entry.lineState}
                  visibleLines={entry.visibleLines}
                  expanded={expandedTodoKeys.has(getSourceKey(entry.todo))}
                  onToggle={() => toggleTodo(entry.todo)}
                  modal={modal}
                />
              ))
            ) : viewMode === VIEW_MODES.FACTORY ? (
              <>
                {hasAnyLineLoading && (
                  <InlineSpinner size={16} text="正在加载明细..." />
                )}
                {factoryGroups.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    暂无可分组的待办项
                  </div>
                ) : (
                  factoryGroups.map((group) => {
                    const expanded = expandedFactoryKeys.has(group.key);
                    return (
                      <div
                        key={group.key}
                        className="border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800"
                      >
                        <div
                          className="px-4 py-3 flex items-center justify-between cursor-pointer"
                          onClick={() => toggleFactory(group.key)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggleFactory(group.key);
                            }
                          }}
                        >
                          <div
                            className={`text-base font-semibold ${group.factory ? "text-gray-900 dark:text-gray-100" : "text-red-600 dark:text-red-400"}`}
                          >
                            {group.displayName}
                          </div>
                          <div className="flex items-center gap-2 text-base text-gray-600 dark:text-gray-400">
                            <span>{group.itemCount} 项</span>
                            <MdExpandMore
                              className={`w-5 h-5 transform transition-transform duration-200 ${
                                expanded
                                  ? "rotate-0 text-blue-500"
                                  : "-rotate-90 text-gray-400"
                              }`}
                            />
                          </div>
                        </div>
                        {expanded && (
                          <div className="px-4 pb-4 space-y-6">
                            {group.entries.map((entry) => (
                              <TodoDetailBlock
                                key={`${group.key}-${getSourceKey(entry.todo)}`}
                                todo={entry.todo}
                                lines={entry.visibleLines}
                                modal={modal}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </>
            ) : (
              <DateTableView
                factoryGroups={factoryGroups}
                dateColumns={dateColumns}
                hasAnyLineLoading={hasAnyLineLoading}
                onOpenDetails={openDateDetails}
              />
            )}
          </div>
        </div>

        <div className="flex-1 relative lg:w-1/3 flex-shrink-0 hidden lg:block">
          <RecentQCReports />
        </div>
      </div>
    </div>
  );
};

const TodoCard = ({
  todo,
  lineState,
  visibleLines,
  expanded,
  onToggle,
  modal,
}) => {
  const deliveryDate = todo.deliveryDate
    ? moment(todo.deliveryDate).format("YYYY-MM-DD")
    : "?";
  const isOverdue =
    !!todo.deliveryDate &&
    moment(todo.deliveryDate).isBefore(moment().startOf("day"));
  const hasResolvedLineCount = !lineState.loading && !lineState.error;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800">
      <div
        className="px-3 md:px-4 py-3 flex items-start md:items-center cursor-pointer gap-2 md:gap-3"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="min-w-0 flex-1 md:flex md:items-center md:gap-3">
          <div
            className={`text-left text-sm md:text-base md:w-28 md:shrink-0 ${isOverdue ? "text-yellow-600 dark:text-yellow-400" : "text-gray-600 dark:text-gray-400"}`}
          >
            {deliveryDate}
          </div>
          <div className="text-left text-base font-semibold text-gray-900 dark:text-gray-100 break-words md:w-36 md:shrink-0 md:truncate">
            {todo.name}
          </div>
          <div className="mt-0.5 md:mt-0 text-left text-sm font-normal text-gray-500 dark:text-gray-400 break-words md:w-56 md:shrink-0 md:truncate">
            {todo.sourceType === "SO" ? todo.customerName || "" : ""}
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-1 md:gap-2 text-sm md:text-base text-gray-600 dark:text-gray-400">
          {lineState.loading ? (
            <InlineSpinner size={16} text={null} />
          ) : (
            hasResolvedLineCount && <span>{visibleLines.length} 项</span>
          )}
          <MdExpandMore
            className={`w-5 h-5 transform transition-transform duration-200 ${
              expanded ? "rotate-0 text-blue-500" : "-rotate-90 text-gray-400"
            }`}
          />
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {lineState.loading ? (
            <InlineSpinner size={16} text="正在加载明细..." />
          ) : lineState.error ? (
            <div className="text-red-500 text-sm">{lineState.error}</div>
          ) : visibleLines.length === 0 ? (
            <div className="text-sm text-gray-500">No product lines.</div>
          ) : (
            visibleLines.map((row) => (
              <TodoRowCard key={row.id} row={row} modal={modal} />
            ))
          )}
        </div>
      )}
    </div>
  );
};

const TodoDetailBlock = ({ todo, lines, modal }) => {
  const deliveryDate = todo.deliveryDate
    ? moment(todo.deliveryDate).format("YYYY-MM-DD")
    : "?";
  const isOverdue =
    !!todo.deliveryDate &&
    moment(todo.deliveryDate).isBefore(moment().startOf("day"));

  return (
    <div className="space-y-3">
      <div className="flex items-start md:items-center gap-2 md:gap-3">
        <div
          className={`w-24 md:w-28 shrink-0 text-left text-sm ${isOverdue ? "text-yellow-600 dark:text-yellow-400" : "text-gray-600 dark:text-gray-400"}`}
        >
          {deliveryDate}
        </div>
        <div className="min-w-0 flex-1 md:flex md:items-center md:gap-3">
          <div className="text-left font-semibold text-gray-900 dark:text-gray-100 break-words md:w-36 md:shrink-0 md:truncate">
            {todo.name}
          </div>
          <div className="mt-0.5 md:mt-0 text-left text-sm font-normal text-gray-500 dark:text-gray-400 break-words md:w-56 md:shrink-0 md:truncate">
            {todo.sourceType === "SO" ? todo.customerName || "" : ""}
          </div>
        </div>
        <div className="shrink-0 text-sm text-gray-500 dark:text-gray-400">
          {lines.length} 项
        </div>
      </div>
      {lines.length === 0 ? (
        <div className="text-sm text-gray-500">No product lines.</div>
      ) : (
        lines.map((row) => <TodoRowCard key={row.id} row={row} modal={modal} />)
      )}
    </div>
  );
};

const TodoRowCard = ({ row, modal }) => {
  const product = row.product;
  const qty = Number(row.todoQty || 0);
  const qtyPerCarton = Number(product?.quantityPerCarton || 0);
  const cartonError = !qtyPerCarton
    ? "缺少 Qty/Carton"
    : qty % qtyPerCarton !== 0
      ? `数量 ${qty} / Qty per carton ${qtyPerCarton} 不能整除`
      : "";
  const cartonQty = cartonError ? null : qty / qtyPerCarton;
  const missingReasons = [];
  if (!row.sourceId || !row.sourceName) {
    missingReasons.push("缺少来源信息");
  }
  if (!product?.id) {
    missingReasons.push("缺少产品映射");
  }
  if (product?.id && !product?.packingFactory?.id) {
    missingReasons.push("缺少包装工厂");
  }
  const blockReason = missingReasons.join("，");
  const canCreate = !!(
    row.sourceId &&
    row.sourceName &&
    product?.id &&
    product?.packingFactory?.id
  );

  return (
    <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl">
      {product?.computedImages?.length ? (
        <ProductImagesV2 images={product.computedImages} />
      ) : null}

      <div className="mt-2 flex items-center gap-2 flex-wrap text-base font-semibold text-gray-900 dark:text-gray-100">
        <span className="font-bold">[{product?.number || "-"}]</span>
        <span>{product?.name || row.odooProductName}</span>
      </div>

      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
        <div>
          数量：<span className="font-bold ml-1">{row.todoQty}</span>
        </div>
        <div>
          包装工厂：
          <span
            className={`font-bold ml-1 ${product?.packingFactory ? "" : "text-red-600 dark:text-red-400"}`}
          >
            {product?.packingFactory?.name || "缺少包装工厂"}
          </span>
        </div>
        <div>
          箱数：
          {cartonError ? (
            <span className="font-bold ml-1 text-red-600 dark:text-red-400">
              异常，{cartonError}
            </span>
          ) : (
            <span className="font-bold ml-1">{cartonQty}</span>
          )}
        </div>
        <div>
          PO：<span className="font-bold ml-1">{row.po || "-"}</span>
        </div>
      </div>

      <div className="mt-3">
        {canCreate ? (
          <Button
            onClick={() =>
              modal.present({
                title: "创建质检报告",
                center: true,
                children: (
                  <CreateReport
                    onCreated={modal.hide}
                    product={product}
                    qty={row.todoQty}
                    sourceOdooId={row.sourceId}
                    sourceLineOdooId={row.id}
                    customerOdooId={row.customerOdooId}
                    sourceName={row.sourceName}
                    invoiceId={row.invoiceId}
                    sourceType={row.sourceType}
                  />
                ),
              })
            }
          >
            + 创建质检报告
          </Button>
        ) : (
          <div className="text-sm text-red-600 font-bold">
            无法创建报告：{blockReason || "数据不完整"}
          </div>
        )}
      </div>
    </div>
  );
};

const DateTableView = ({
  factoryGroups,
  dateColumns,
  hasAnyLineLoading,
  onOpenDetails,
}) => {
  const today = moment().startOf("day");
  const getDateKey = (raw) =>
    raw ? moment(raw).format("YYYY-MM-DD") : "unknown";

  if (hasAnyLineLoading) {
    return <InlineSpinner size={16} text="正在加载明细..." />;
  }

  if (factoryGroups.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        暂无可分组的待办项
      </div>
    );
  }

  return (
    <div className="relative overflow-x-auto">
      <table className="min-w-full border-collapse text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th
              className="px-3 py-2 text-left text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-800 w-32 min-w-[8rem] whitespace-normal break-words"
              style={{ position: "sticky", left: 0, zIndex: 2 }}
            >
              包装工厂
            </th>
            {dateColumns.map((col) => {
              const isDue =
                col.raw && moment(col.raw).isSameOrBefore(today, "day");
              const headerClass = isDue
                ? "text-yellow-600 dark:text-yellow-400"
                : "text-gray-600 dark:text-gray-300";
              return (
                <th
                  key={col.key}
                  className={`px-3 py-2 text-center whitespace-nowrap ${headerClass}`}
                >
                  {col.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {factoryGroups.map((group) => (
            <tr key={group.key}>
              <td
                className="px-3 py-3 font-semibold whitespace-normal break-words bg-white dark:bg-gray-900 w-32 min-w-[8rem]"
                style={{ position: "sticky", left: 0, zIndex: 1 }}
              >
                <span
                  className={
                    group.factory
                      ? "text-gray-900 dark:text-gray-100"
                      : "text-red-600 dark:text-red-400"
                  }
                >
                  {group.displayName}
                </span>
              </td>
              {dateColumns.map((col) => {
                const count = group.entries.reduce((sum, entry) => {
                  if (getDateKey(entry.todo.deliveryDate) !== col.key)
                    return sum;
                  return sum + entry.visibleLines.length;
                }, 0);
                const clickable = count > 0;

                return (
                  <td
                    key={`${group.key}-${col.key}`}
                    className="px-3 py-2 text-center align-middle"
                    onClick={() =>
                      clickable ? onOpenDetails(group, col) : undefined
                    }
                  >
                    <span
                      className={`inline-flex items-center justify-center px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                        clickable
                          ? "cursor-pointer text-blue-700 hover:bg-blue-100 dark:text-blue-200"
                          : "text-gray-400 dark:text-gray-500 cursor-default"
                      }`}
                    >
                      {count > 0 ? `${count} 项` : "—"}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Dashboard;
